import {
  ArrowLeftIcon,
  ArrowRightIcon,
  DownloadIcon,
} from '@radix-ui/react-icons';
import {
  Badge,
  Box,
  Card,
  Flex,
  Heading,
  SegmentedControl,
} from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { BlurOverlay } from 'components/common/blur-overlay';
import { CommonBackArrow } from 'components/common/buttons/back-arrow';
import { DialogButton } from 'components/common/dialogs/button';
import FloatingCopyButton from 'components/common/form/floating-copy-button';
import { JsonViewer } from 'components/common/json/json-viewer';
import { CommonTooltip } from 'components/common/tooltip';
import {
  AnnotatedVideoPlayer,
  IAnnotationDetails,
} from 'components/common/video/annotated-player';
import { formatDate } from 'components/sections/admin-portal/machines/dialogs/settings/machine-shot-history/common';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { StereoVideoType } from 'lib_ts/enums/videos.enums';
import { IFireEvent } from 'lib_ts/interfaces/i-session-event';
import { IStereoVideo } from 'lib_ts/interfaces/i-stereo-video';
import { compact, first, uniq } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { StereoVideosService } from 'services/stereo-videos.service';

interface IFireDetailsProps {
  fire: IFireEvent;
  hasPrev: boolean;
  hasNext: boolean;
  onBack: () => void;
  onPrevFire: () => void;
  onNextFire: () => void;
}

const getDefaultVideoType = (fire: IFireEvent) => {
  if (fire.annotated_video_id) {
    return StereoVideoType.Annotated;
  }

  if (fire.replay_video_id) {
    return StereoVideoType.Replay;
  }

  if (fire.raw_video_id) {
    return StereoVideoType.Raw;
  }

  if (fire.spins_video_id) {
    return StereoVideoType.Spins;
  }
};

export const MachineFireDetails = (props: IFireDetailsProps) => {
  const { fire, hasNext, hasPrev, onBack } = props;
  const { data } = fire;

  const { pitch_name } = data;

  // for linking to the video after it loads
  const [playbackUrl, setPlaybackUrl] = useState<string>();

  const [videoData, setVideoData] = useState<IAnnotationDetails>();

  const [videoType, setVideoType] = useState(getDefaultVideoType(fire));

  const [videos, setVideos] = useState<IStereoVideo[]>([]);

  const annotatedVideo = useMemo(
    () => videos.find((m) => m._id === fire.annotated_video_id),
    [videos, fire.annotated_video_id]
  );

  const replayVideo = useMemo(
    () => videos.find((m) => m._id === fire.replay_video_id),
    [videos, fire.replay_video_id]
  );

  const rawVideo = useMemo(
    () => videos.find((m) => m._id === fire.raw_video_id),
    [videos, fire.raw_video_id]
  );

  const spinsVideo = useMemo(
    () => videos.find((m) => m._id === fire.spins_video_id),
    [videos, fire.spins_video_id]
  );

  const numVideos = videos.length;

  // reload videos whenever the fire changes
  useEffect(() => {
    setVideoType(getDefaultVideoType(fire));

    const getFireVideos = async () => {
      // ? empty values will be omitted
      const videoIDs = compact(
        uniq([
          fire.annotated_video_id,
          fire.replay_video_id,
          fire.raw_video_id,
          fire.spins_video_id,
        ])
      );

      if (videoIDs.length === 0) {
        setVideos([]);
        return;
      }

      const results = await StereoVideosService.getInstance().getMany(videoIDs);
      setVideos(results);
    };

    getFireVideos();
  }, [
    fire._id,
    fire.annotated_video_id,
    fire.replay_video_id,
    fire.raw_video_id,
    fire.spins_video_id,
  ]);

  // update the loaded video whenever the selected type (or its video) changes
  useEffect(() => {
    const selectedVideo = (() => {
      switch (videoType) {
        case StereoVideoType.Annotated:
          return annotatedVideo;
        case StereoVideoType.Replay:
          return replayVideo;
        case StereoVideoType.Raw:
          return rawVideo;
        case StereoVideoType.Spins:
          return spinsVideo;
        default:
          return undefined;
      }
    })();

    if (!selectedVideo) {
      setVideoData(undefined);
      return;
    }

    setVideoData({
      videoID: selectedVideo._id,
      fireID: fire._id,
      shotID: first(fire.shot)?._id,
      hitterID: fire.data.hitterExt?._id,
    });
  }, [
    videoType,
    annotatedVideo,
    replayVideo,
    rawVideo,
    spinsVideo,
    fire._id,
    fire.shot,
    fire.data.hitterExt?._id,
  ]);

  return (
    <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
      <Flex gap={RADIX.FLEX.GAP.SM} align="center">
        <CommonBackArrow onClick={onBack} />
        <CommonTooltip
          text="Click to copy this fire ID to the clipboard"
          trigger={
            <Heading
              className="cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();

                navigator.clipboard.writeText(fire._id);
                NotifyHelper.success({
                  message_md: `Fire ID ${fire._id} copied to clipboard!`,
                });
              }}
              size={RADIX.TEXT.SIZE.LG}
            >
              {pitch_name}
            </Heading>
          }
        />
        <Badge color={RADIX.COLOR.NEUTRAL}>{formatDate(fire._created)}</Badge>
        <Flex gap={RADIX.FLEX.GAP.SM} ml="auto">
          <DialogButton
            icon={<ArrowLeftIcon />}
            label="Next"
            onClick={props.onNextFire}
            disabled={!hasNext}
          />
          <DialogButton
            suffixIcon={<ArrowRightIcon />}
            label="Previous"
            onClick={props.onPrevFire}
            disabled={!hasPrev}
          />
        </Flex>
      </Flex>

      <Box mb="2" position="relative">
        {videoData && (
          <AnnotatedVideoPlayer
            details={videoData}
            onLoadUrl={(url) => setPlaybackUrl(url)}
            extraControls={
              numVideos > 0 && (
                <SegmentedControl.Root
                  value={videoType}
                  style={{ background: 'var(--gray-3)' }}
                  onValueChange={(v) => setVideoType(v as StereoVideoType)}
                  size="1"
                >
                  {annotatedVideo && (
                    <SegmentedControl.Item
                      value={StereoVideoType.Annotated}
                      className="cursor-pointer"
                    >
                      Annotated
                    </SegmentedControl.Item>
                  )}
                  {replayVideo && (
                    <SegmentedControl.Item
                      value={StereoVideoType.Replay}
                      className="cursor-pointer"
                    >
                      Replay
                    </SegmentedControl.Item>
                  )}
                  {rawVideo && (
                    <SegmentedControl.Item
                      value={StereoVideoType.Raw}
                      className="cursor-pointer"
                    >
                      Raw
                    </SegmentedControl.Item>
                  )}
                  {spinsVideo && (
                    <SegmentedControl.Item
                      value={StereoVideoType.Spins}
                      className="cursor-pointer"
                    >
                      Spin
                    </SegmentedControl.Item>
                  )}
                </SegmentedControl.Root>
              )
            }
            autoplay
          />
        )}
      </Box>
      <Flex direction="column" position="relative">
        {videoData && (
          <Flex justify="end">
            <DialogButton
              onClick={() => {
                if (!playbackUrl) {
                  return;
                }

                window.open(playbackUrl, '_blank');
              }}
              label="Download Video"
              icon={<DownloadIcon />}
              variant="solid"
            />
          </Flex>
        )}
        <BlurOverlay show={!playbackUrl} />
      </Flex>
      <Box position="relative">
        <Card>
          <JsonViewer value={fire} />
        </Card>
        {fire && (
          <FloatingCopyButton
            value={JSON.stringify(fire, null, 2)}
            label="Machine Fire"
          />
        )}
      </Box>
    </Flex>
  );
};
